import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Grid,
  Typography,
  Skeleton,
  Button,
  Chip,
  Stack,
  Drawer,
  Dialog,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FilterListIcon from "@mui/icons-material/FilterList";
import api from "../../../../services/api";
import { useWeddingWizard } from "../../../../context/WeddingWizardContext";
import { useAppConfig } from "../../../../context/AppConfigContext";
import BannerInfo from "./BannerInfo";
import StyleCard from "./StyleCard";
import FilterPanel from "./FilterPanel";
import useElementVisibility from "./useElementVisibility";
import FloatingFilterFab from "./FloatingFilterFab";

export default function StylePickerStep() {
  const { state, setCakeStyleId, next, setCakeInclude, setCakeTiers } =
    useWeddingWizard();
  const { config } = useAppConfig();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    shape: "any",
    buttercreamFinish: "any",
    palette: [],
    themes: [],
    floralKinds: [],
    floralColors: [],
    accents: [],
  });
  const [panelOpen, setPanelOpen] = useState(false);

  const headerRef = useRef(null);
  const headerVisible = useElementVisibility(headerRef);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/weddings/styles");
        if (mounted) setStyles(Array.isArray(data?.styles) ? data.styles : []);
      } catch (e) {
        console.error("Failed to load styles", e);
        if (mounted) setStyles([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const enums = useMemo(() => deriveEnumsFromStyles(styles), [styles]);

  const activeCount = useMemo(() => {
    const f = filters;
    return (
      (f.shape !== "any") +
      (f.buttercreamFinish !== "any") +
      (f.palette.length > 0) +
      (f.themes.length > 0) +
      (f.floralKinds.length > 0) +
      (f.floralColors.length > 0) +
      (f.accents.length > 0)
    );
  }, [filters]);

  const filtered = useMemo(() => {
    const hasAll = (need = [], have = []) =>
      !need.length ||
      need.every((t) => new Set((have || []).map(String)).has(String(t)));
    return (styles || []).filter((s) => {
      const d = s.design || {};
      if (filters.shape !== "any" && d.shape !== filters.shape) return false;
      if (
        filters.buttercreamFinish !== "any" &&
        d.buttercreamFinish !== filters.buttercreamFinish
      )
        return false;
      if (!hasAll(filters.palette, d.palette)) return false;
      if (!hasAll(filters.themes, d.themes)) return false;
      if (!hasAll(filters.floralKinds, d.florals?.kinds)) return false;
      if (!hasAll(filters.floralColors, d.florals?.colors)) return false;
      if (!hasAll(filters.accents, d.accents)) return false;
      return true;
    });
  }, [styles, filters]);

  const handlePick = useCallback(
    (id) => {
      if (!state.cake?.include) setCakeInclude(true);
      if (state.cake?.styleId && state.cake.styleId !== id) setCakeTiers([]);
      const doc = styles.find(
        (s) => String(s._id || s.id || s.styleId) === String(id)
      );
      const label =
        doc?.displayName ||
        doc?.name ||
        doc?.title ||
        doc?.label ||
        "Selected style";
      setCakeStyleId({ id: String(id), name: label });
      next();
    },
    [
      next,
      setCakeInclude,
      setCakeStyleId,
      setCakeTiers,
      state.cake?.include,
      state.cake?.styleId,
      styles,
    ]
  );

  const resetFilters = () =>
    setFilters({
      shape: "any",
      buttercreamFinish: "any",
      palette: [],
      themes: [],
      floralKinds: [],
      floralColors: [],
      accents: [],
    });

  return (
    <Box>
      <Box
        ref={headerRef}
        sx={{
          mb: 2,
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
          Choose Style
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            size="small"
            onClick={() => setPanelOpen(true)}
            sx={{
              fontWeight: 600,
              // keep button compact on mobile
              px: { xs: 1.25, sm: 2 },
              py: { xs: 0.5, sm: 0.75 },
            }}>
            Filters
          </Button>

          {activeCount > 0 && (
            <Chip
              label={`${activeCount} active`}
              color="primary"
              size="small"
              onDelete={resetFilters}
            />
          )}
        </Box>
      </Box>

      <BannerInfo sx={{ mb: 2 }} />

      {/* Desktop: centered dialog. Mobile: bottom drawer. */}
      {mdUp ? (
        <Dialog
          open={panelOpen}
          onClose={() => setPanelOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}>
          <FilterPanel
            variant="dialog"
            value={filters}
            onChange={setFilters}
            enums={enums}
            onClose={() => setPanelOpen(false)}
            onClear={resetFilters}
          />
        </Dialog>
      ) : (
        <Drawer
          anchor="bottom"
          open={panelOpen}
          onClose={() => setPanelOpen(false)}
          PaperProps={{
            sx: {
              p: 0,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: "85vh",
            },
          }}>
          <FilterPanel
            variant="drawer"
            value={filters}
            onChange={setFilters}
            enums={enums}
            onClose={() => setPanelOpen(false)}
            onClear={resetFilters}
          />
        </Drawer>
      )}

      {/* Styles grid */}
      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid key={i} item xs={12} sm={6} md={4}>
              <Skeleton
                variant="rectangular"
                height={260}
                sx={{ borderRadius: 2 }}
              />
              <Skeleton width="60%" sx={{ mt: 1 }} />
              <Skeleton width="40%" />
            </Grid>
          ))}
        </Grid>
      ) : !filtered.length ? (
        <Typography variant="body1">No styles match your filters.</Typography>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((s) => (
            <Grid key={s._id} item xs={12} sm={6} md={4}>
              <StyleCard
                styleDoc={s}
                fallbackImage={config?.weddings?.gallery?.[0] ?? ""}
                onPick={() => handlePick(s._id)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1.5 }}>
        Don’t see your dream design? We are a message away - send us a sample
        picture at contact@cakeyhand.com
      </Typography>

      <FloatingFilterFab
        visible={!headerVisible && !panelOpen}
        onClick={() => setPanelOpen(true)}
      />
    </Box>
  );
}

function deriveEnumsFromStyles(styles) {
  const sets = {
    shapes: new Set(),
    finishes: new Set(),
    palette: new Set(),
    themes: new Set(),
    floralKinds: new Set(),
    floralColors: new Set(),
    accents: new Set(),
  };
  (styles || []).forEach((s) => {
    const d = s.design || {};
    if (d.shape) sets.shapes.add(d.shape);
    if (d.buttercreamFinish) sets.finishes.add(d.buttercreamFinish);
    (d.palette || []).forEach((x) => sets.palette.add(x));
    (d.themes || []).forEach((x) => sets.themes.add(x));
    (d.florals?.kinds || []).forEach((x) => sets.floralKinds.add(x));
    (d.florals?.colors || []).forEach((x) => sets.floralColors.add(x));
    (d.accents || []).forEach((x) => sets.accents.add(x));
  });
  const FB = {
    shapes: ["round", "square", "mixed", "hexagon", "heart"],
    finishes: ["smooth", "textured", "semi_naked"],
    palette: [
      "white",
      "ivory",
      "neutral",
      "pastel",
      "bold",
      "dark",
      "blush",
      "pink",
      "red",
      "burgundy",
      "gold",
      "silver",
      "sage",
      "green",
      "navy",
      "blue",
      "black",
    ],
    themes: [
      "classic",
      "modern_minimal",
      "rustic_boho",
      "floral_forward",
      "glam_metallic",
      "vintage",
      "geometric",
      "whimsical",
      "watercolor_painted",
    ],
    floralKinds: ["fresh", "artificial", "sugar", "dried"],
    floralColors: ["white_ivory", "blush", "greenery_heavy"],
    accents: [
      "metallic_gold",
      "metallic_silver",
      "metallic_rose",
      "pearls",
      "ruffles",
      "stencil",
      "lace",
      "wafer_paper",
      "acrylic_separator",
      "hand_painting",
    ],
  };
  const pick = (s, fb) => (s.size ? [...s].sort() : fb);
  return {
    shapes: pick(sets.shapes, FB.shapes),
    finishes: pick(sets.finishes, FB.finishes),
    palette: pick(sets.palette, FB.palette),
    themes: pick(sets.themes, FB.themes),
    floralKinds: pick(sets.floralKinds, FB.floralKinds),
    floralColors: pick(sets.floralColors, FB.floralColors),
    accents: pick(sets.accents, FB.accents),
  };
}
